import React, { useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { GET_DEPLOYMENT_DETAIL } from '../../graphql/portalHealth'
import { isCriticalLogLine } from '../../utils/portalLogCatalog'
import './PortalHealth.css'

function formatTs(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-CL', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

function shortPodName(name) {
  if (!name) return '—'
  const parts = name.split('-')
  if (parts.length <= 2) return name
  return `${parts.slice(0, -2).join('-')}-…${parts.slice(-1)[0]}`
}

function shortImage(image) {
  if (!image) return null
  const tag = image.includes(':') ? image.split(':').pop() : image
  const repo = image.split('/').pop()?.split(':')[0] || image
  return { repo, tag, full: image }
}

function InfoTip({ text }) {
  if (!text) return null
  return (
    <span className="ph-info">
      <button
        type="button"
        className="ph-info-btn"
        aria-label="Más información"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
      >
        i
      </button>
      <span className="ph-info-pop" role="tooltip">
        {text}
      </span>
    </span>
  )
}

function buildCauseLines(detail) {
  if (!detail) return []
  const lines = []
  const notReady = (detail.pods || []).filter((p) => !p.ready)
  for (const p of notReady.slice(0, 4)) {
    const bit = [p.reason, p.message].filter(Boolean).join(' — ')
    lines.push(
      bit
        ? `${shortPodName(p.name)}: ${bit}`
        : `${shortPodName(p.name)}: phase=${p.phase || '?'} (not Ready)`,
    )
  }
  const warnings = (detail.events || []).filter((e) => e.type === 'Warning')
  for (const ev of warnings.slice(0, 3)) {
    lines.push(
      `${ev.reason}${ev.objectName ? ` · ${shortPodName(ev.objectName)}` : ''}: ${(ev.message || '').slice(0, 140)}`,
    )
  }
  return [...new Set(lines)].slice(0, 5)
}

function DrillSkeleton() {
  return (
    <div className="ph-drill-skel" aria-busy="true" aria-label="Cargando detalle">
      <div className="ph-drill-skel-stats">
        <span className="ph-skel ph-skel--chip" />
        <span className="ph-skel ph-skel--chip" />
        <span className="ph-skel ph-skel--chip" />
      </div>
      <div className="ph-drill-skel-grid">
        <div className="ph-drill-panel">
          <div className="ph-skel ph-skel--title" />
          <div className="ph-skel ph-skel--row" />
          <div className="ph-skel ph-skel--row" />
          <div className="ph-skel ph-skel--row" />
        </div>
        <div className="ph-drill-panel">
          <div className="ph-skel ph-skel--title" />
          <div className="ph-skel ph-skel--row" />
          <div className="ph-skel ph-skel--row" />
          <div className="ph-skel ph-skel--row" />
        </div>
      </div>
    </div>
  )
}

function PodRow({ pod }) {
  const bad = !pod.ready
  return (
    <li className={`ph-pod ${bad ? 'ph-pod--bad' : 'ph-pod--ok'}`}>
      <span className={`ph-pod-dot ${bad ? 'ph-pod-dot--bad' : 'ph-pod-dot--ok'}`} />
      <div className="ph-pod-main">
        <code className="ph-pod-name" title={pod.name}>
          {shortPodName(pod.name)}
        </code>
        <span className="ph-pod-meta">
          {pod.phase}
          {pod.restarts > 0 ? ` · ${pod.restarts} restarts` : ''}
          {pod.nodeName ? ` · ${pod.nodeName.split('.').slice(0, 2).join('.')}` : ''}
        </span>
        {bad && (pod.reason || pod.message) ? (
          <span className="ph-pod-issue">
            {[pod.reason, pod.message].filter(Boolean).join(' — ')}
          </span>
        ) : null}
      </div>
      <span className={`ph-pod-ready ${pod.ready ? 'ph-pod-ready--yes' : 'ph-pod-ready--no'}`}>
        {pod.ready ? 'Ready' : 'NotReady'}
      </span>
    </li>
  )
}

function EventRow({ ev }) {
  const warn = ev.type === 'Warning'
  return (
    <li className={`ph-ev ${warn ? 'ph-ev--warn' : ''}`}>
      <span className="ph-ev-time">{formatTs(ev.lastTimestamp)}</span>
      <div className="ph-ev-body">
        <div className="ph-ev-top">
          <span className={`ph-ev-badge ${warn ? 'ph-ev-badge--warn' : 'ph-ev-badge--ok'}`}>
            {ev.reason || ev.type}
          </span>
          {ev.objectName ? (
            <code className="ph-ev-obj" title={ev.objectName}>
              {shortPodName(ev.objectName)}
            </code>
          ) : null}
          {ev.count > 1 ? <span className="ph-ev-count">×{ev.count}</span> : null}
        </div>
        {ev.message ? <p className="ph-ev-msg">{ev.message}</p> : null}
      </div>
    </li>
  )
}

function classifyLogLine(line) {
  if (/\b(ERROR|CRIT|FATAL|panic|Erlang error)\b/i.test(line)) return 'err'
  if (/\b(WARN|WARNING)\b/i.test(line)) return 'warn'
  return 'ok'
}

function PodLogPanel({ logs }) {
  const [podName, setPodName] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const entries = logs || []

  const active = useMemo(() => {
    if (!entries.length) return null
    const pick =
      entries.find((l) => l.podName === podName) ||
      entries.find((l) => l.error) ||
      entries[0]
    return pick
  }, [entries, podName])

  const allLines = useMemo(() => {
    return (active?.text || '')
      .split('\n')
      .filter((l, i, arr) => l.length > 0 || i < arr.length - 1)
      .slice(-200)
  }, [active])

  const criticalLines = useMemo(
    () => allLines.filter((l) => isCriticalLogLine(l)),
    [allLines],
  )

  const lines = showAll ? allLines : criticalLines

  if (!entries.length) {
    return (
      <p className="ph-empty">
        Sin logs de pod (aplica pods/log en RBAC o espera pods Ready)
      </p>
    )
  }

  return (
    <div className="ph-log-panel">
      <div className="ph-log-pods" role="tablist" aria-label="Pods">
        {entries.map((l) => {
          const selected = active?.podName === l.podName
          return (
            <button
              key={l.podName}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`ph-log-pod-btn${selected ? ' ph-log-pod-btn--on' : ''}${
                l.error ? ' ph-log-pod-btn--err' : ''
              }`}
              onClick={() => setPodName(l.podName)}
              title={l.podName}
            >
              {shortPodName(l.podName)}
            </button>
          )
        })}
      </div>
      {active?.error ? (
        <p className="ph-log-error" role="alert">
          No se pudo leer log de <code>{active.podName}</code>
          {active.container ? ` (${active.container})` : ''}: {active.error}
        </p>
      ) : null}
      <div className="ph-log-meta">
        <span>
          {showAll
            ? `Últimas ${active?.tailLines || '—'} líneas`
            : `${criticalLines.length} críticas de ${allLines.length}`}
          {active?.container ? ` · container ${active.container}` : ''}
          {' · '}
          vía API K8s (no Loki)
        </span>
        <button
          type="button"
          className="ph-link-btn"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Solo críticas' : 'Ver todo el tail'}
        </button>
      </div>
      <pre className="ph-log-pre" tabIndex={0}>
        {lines.length === 0 && !active?.error ? (
          <span className="ph-log-line ph-log-line--ok">
            {showAll
              ? '(vacío)'
              : '(sin líneas críticas en el tail — prueba Ver todo el tail)'}
          </span>
        ) : (
          lines.map((line, i) => (
            <span
              key={`${i}-${line.slice(0, 24)}`}
              className={`ph-log-line ph-log-line--${classifyLogLine(line)}`}
            >
              {line}
              {'\n'}
            </span>
          ))
        )}
      </pre>
    </div>
  )
}

export function FleetDrilldown({ name, namespace, embedded = false }) {
  const [showNormalEvents, setShowNormalEvents] = useState(false)
  const [result] = useQuery({
    query: GET_DEPLOYMENT_DETAIL,
    variables: { name, namespace },
    requestPolicy: 'network-only',
    pause: !name || !namespace,
  })

  const detail = result.data?.portalHealthDeploymentDetail
  const fetching = result.fetching
  const error = result.error
  const causeLines = useMemo(() => buildCauseLines(detail), [detail])
  const img = shortImage(detail?.image)

  const warningEvents = useMemo(
    () => (detail?.events || []).filter((e) => e.type === 'Warning'),
    [detail],
  )
  const normalEvents = useMemo(
    () => (detail?.events || []).filter((e) => e.type !== 'Warning'),
    [detail],
  )
  const visibleEvents = showNormalEvents
    ? [...warningEvents, ...normalEvents].slice(0, 12)
    : warningEvents.slice(0, 8)

  const capacityLabel =
    detail == null
      ? null
      : detail.desired > 0 && detail.ready === 0
        ? 'Caído'
        : detail.desired > 0 && detail.ready < detail.desired
          ? 'Degradado'
          : 'OK'

  return (
    <div
      className={`ph-drill${embedded ? ' ph-drill--embedded' : ''}`}
      role="region"
      aria-label={`Detalle ${name}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="ph-drill-head">
        <div className="ph-drill-head-text">
          {!embedded ? (
            <p className="ph-kicker">
              {namespace}/{name}
            </p>
          ) : (
            <p className="ph-drill-kicker">Detalle · {namespace}</p>
          )}
          {img ? (
            <p className="ph-drill-image" title={img.full}>
              <span className="ph-drill-image-repo">{img.repo}</span>
              <span className="ph-drill-image-tag">:{img.tag}</span>
            </p>
          ) : fetching ? (
            <span className="ph-skel ph-skel--line" />
          ) : null}
        </div>
        {name === 'rabbitmq-ha' ? (
          <a
            className="ph-btn ph-btn-link"
            href="https://rabbit.smartcfo.cl/"
            target="_blank"
            rel="noopener noreferrer"
            title="Abre Rabbit Management UI"
          >
            Management
          </a>
        ) : null}
      </div>

      {error ? (
        <div className="ph-error" role="alert">
          {error.message}
        </div>
      ) : null}

      {fetching && !detail ? <DrillSkeleton /> : null}

      {detail ? (
        <div className="ph-drill-body">
          <div className="ph-drill-stats" aria-label="Resumen">
            <span className="ph-stat">
              <strong>
                {detail.ready}/{detail.desired}
              </strong>{' '}
              Ready
            </span>
            {detail.unavailable > 0 ? (
              <span className="ph-stat ph-stat--warn">
                <strong>{detail.unavailable}</strong> unavailable
              </span>
            ) : null}
            <span className="ph-stat">
              <strong>{detail.pods.length}</strong> pods
            </span>
            <span
              className={`ph-stat ph-stat--pill ph-stat--pill-${
                capacityLabel === 'OK' ? 'ok' : capacityLabel === 'Degradado' ? 'warn' : 'err'
              }`}
            >
              {capacityLabel}
            </span>
          </div>

          {causeLines.length > 0 ? (
            <div className="ph-drill-cause" role="status">
              <strong>Qué mirar</strong>
              <ul>
                {causeLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="ph-drill-grid">
            <section className="ph-drill-panel">
              <header className="ph-drill-panel-head">
                <h4>Pods</h4>
                <span className="ph-drill-panel-count">{detail.pods.length}</span>
              </header>
              {detail.pods.length === 0 ? (
                <p className="ph-empty">Sin pods (label app={name})</p>
              ) : (
                <ul className="ph-pod-list">
                  {detail.pods.map((p) => (
                    <PodRow key={p.name} pod={p} />
                  ))}
                </ul>
              )}
            </section>

            <section className="ph-drill-panel">
              <header className="ph-drill-panel-head">
                <h4>Events</h4>
                <span className="ph-drill-panel-count">
                  {warningEvents.length > 0
                    ? `${warningEvents.length} warn`
                    : `${detail.events.length}`}
                </span>
              </header>

              {warningEvents.length === 0 && !showNormalEvents ? (
                <p className="ph-drill-quiet">
                  Sin warnings recientes.
                  {normalEvents.length > 0 ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="ph-link-btn"
                        onClick={() => setShowNormalEvents(true)}
                      >
                        Ver {Math.min(normalEvents.length, 12)} Normal
                      </button>
                    </>
                  ) : (
                    ' Sin events (o falta RBAC).'
                  )}
                </p>
              ) : (
                <>
                  <ul className="ph-ev-list">
                    {visibleEvents.map((ev, i) => (
                      <EventRow key={`${ev.reason}-${ev.objectName}-${i}`} ev={ev} />
                    ))}
                  </ul>
                  {normalEvents.length > 0 ? (
                    <button
                      type="button"
                      className="ph-link-btn ph-link-btn--block"
                      onClick={() => setShowNormalEvents((v) => !v)}
                    >
                      {showNormalEvents
                        ? 'Ocultar events Normal'
                        : `Mostrar Normal (${normalEvents.length})`}
                    </button>
                  ) : null}
                </>
              )}
            </section>
          </div>

          <section className="ph-drill-panel ph-drill-panel--logs">
            <header className="ph-drill-panel-head">
              <h4>
                Logs del pod
                <InfoTip text="Tail en vivo vía API de Kubernetes (últimas líneas del pod)." />
              </h4>
              <span className="ph-drill-panel-count">
                {(detail.logs || []).length} pod
                {(detail.logs || []).length === 1 ? '' : 's'}
              </span>
            </header>
            <PodLogPanel logs={detail.logs || []} />
          </section>
        </div>
      ) : null}
    </div>
  )
}

export default FleetDrilldown
